import { useState, useEffect, useRef } from 'react';
import { api } from '../api';

export default function ClientAutocomplete({ value, onChange, onSelect, placeholder }) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleInput(e) {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    setActiveIdx(-1);

    clearTimeout(debounceRef.current);
    if (val.trim().length < 2) { setResults([]); setOpen(false); return; }

    debounceRef.current = setTimeout(async () => {
      try {
        const data = await api.searchClients(val.trim());
        setResults(data);
        setOpen(data.length > 0);
      } catch (e) {
        setResults([]);
      }
    }, 250);
  }

  function pick(item) {
    setQuery(item.ClientName);
    onChange(item.ClientName);
    onSelect(item);
    setOpen(false);
  }

  function handleKeyDown(e) {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      pick(results[activeIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={query}
        onChange={handleInput}
        onFocus={() => { if (results.length > 0) setOpen(true); }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || 'Search clients...'}
        autoComplete="off"
        style={{ width: '100%', boxSizing: 'border-box' }}
      />
      {open && results.length > 0 && (
        <div className="autocomplete-dropdown">
          {results.map((r, i) => (
            <div
              key={`${r.source}-${r.ClientId || r.ProcoreVendorId}`}
              className={`autocomplete-option ${i === activeIdx ? 'active' : ''}`}
              onMouseEnter={() => setActiveIdx(i)}
              onMouseDown={() => pick(r)}
            >
              <div className="autocomplete-option-name">{r.ClientName}</div>
              <div className="autocomplete-option-meta">
                {r.source === 'bidboard' && <span className="autocomplete-tag tag-bidboard">BidBoard</span>}
                {r.source === 'procore' && <span className="autocomplete-tag tag-procore">Procore</span>}
                {r.City && r.State && <span>{r.City}, {r.State}</span>}
                {r.Phone && <span>{r.Phone}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
